import { APP_BASE_HREF } from '@angular/common';
import { Type } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import {
  APP_DEBUG,
  APP_STAGE,
  APP_TENANT,
} from '@nibtravel/shared/util/app-tenancy';

/**
 * Simple wrapper that uses the http response object to
 * obtain the header values, validate them and transform
 * into the Angular StaticProvider[].
 *
 * @param response The http response object
 * @returns undefined when any of the expected headers is not present
 */
const getProviders = (response: Response) => {
  const config = configFromHeaders(response);
  return isConfigValid(config)
    ? {
        config,
        providers: [
          { provide: APP_BASE_HREF, useValue: config.baseHref },
          { provide: APP_TENANT, useValue: config.brand },
          { provide: APP_DEBUG, useValue: config.debug },
          { provide: APP_STAGE, useValue: config.stage },
        ],
      }
    : { config };
};

const safeBoolParse = (value: string | null | undefined) => {
  return value ? value.toLowerCase().trim() === 'true' : false;
};

/**
 * Simple abstraction that parse the expected headers
 * into a consumable object.
 *
 * Brand and stage MUST BE PROVIDED within the headers
 * BaseHref sets default '/' when not provided.
 *
 * @param response The http response object
 * @returns
 */
const configFromHeaders = ({ headers }: Response) => {
  const brand = headers.get('x-tenant-id');
  const debug = safeBoolParse(headers.get('x-debug-log'));
  const stage = headers.get('x-environment-id');
  let baseHref = headers.get('x-base-href') || '/';
  if (!baseHref.endsWith('/')) baseHref += '/';

  const result = { brand, debug, stage, baseHref };
  if (debug) {
    console.log('Response headers: ', result);
  }

  return result;
};

/**
 * Simple abstraction that checks if app properties have been provided.
 *
 * @param config
 * @returns
 */
const isConfigValid = (config: {
  brand: string | null;
  debug: boolean;
  stage: string | null;
  baseHref: string | null;
}) => Object.values(config).every((value) => value != null);

/**
 * Bootstrap an Angular application with multi-tenancy tokens.
 * This wrapper performs a get request to itself to be
 * able to fetch the request headers.
 *
 * It expects the following headers:
 *  - x-tenant-id => The brand code. e.g.: tidau
 *  - x-environment-id => The execution stage. e.g.: kaos
 *  - x-base-href => The base href on which the app should be executed.
 *
 * The x-base-href is a new header that is used for widget apps.
 * The main idea is that the app would be loaded from a host application.
 * When this happens, the widget is invoked by the host that is going to be different
 * from the where the widget is actually hosted.
 *
 * e.g.
 * Host: https://buy.travelinsurancedirect.com.au
 * widget: https://qqc-web.travelinsurancedirect.com.au
 * baseHref: https://qqc-web.travelinsurancedirect.com.au/
 *
 * The host invokes the widget, the base href MUST be the widget url so then the internal
 * assets of the widget can be loaded from the correct location. Otherwise, if it's the default ('/')
 * it would try to load assets such as /styles.css or /font-name.woff using the host url.
 *
 * @param appModule The application AppModule
 */
export const bootstrapAppWithTenancy = (
  appModule: Type<unknown>,
  throwIfError = false
) => {
  const { requestHref, requestMethod } = window as any;
  const request = new Request(requestHref || '/', {
    method: requestMethod || 'GET',
  });
  fetch(request)
    .then((response) => {
      const result = getProviders(response);
      if (result.providers) {
        const head = document.getElementsByTagName('head')[0];
        if (head) {
          const style = document.createElement('link');
          style.rel = 'stylesheet';
          style.type = 'text/css';
          style.href = `${result.config.baseHref}styles.css`;
          head.appendChild(style);
        }

        platformBrowserDynamic(result.providers)
          .bootstrapModule(appModule)
          .catch((err) => console.error(err));
      } else if (result.config?.debug) {
        console.log('One or more headers are missing.');
        console.log('Application will not be bootstrapped...');
      }
    })
    .catch((error) => {
      if (throwIfError) {
        throw new Error(error);
      } else {
        console.warn('An error has occurred bootstrapping the app.');
      }
    });
};
